import React, { useId, useState } from 'react';
import { Button, Card, Input, Text } from '@fragments';
import { IconVideo, IconVideoPlus } from '@tabler/icons-react';
import memoStudio from '../../../../utils/memoStudio';
import './styles.scss';

// Recording notes stay visible in a well inside their block, read as text and edited
// in place on click. A block without notes only offers to add one from its header.
// `readOnly` (an older revision on screen) shows the same block without edits.
function ScriptBlock({ studio: s, block, index, readOnly = false }) {
  const { t } = s;
  const notesId = useId();
  const [editing, setEditing] = useState(false);
  const kind = t(block.kind);
  const notes = block.recording_notes || '';
  const hasNotes = editing || Boolean(notes.trim());
  const caretToEnd = event => event.target.setSelectionRange(event.target.value.length, event.target.value.length);
  const note = <><IconVideo className="studio-script-block__notes-icon" aria-hidden="true" /><Text.span id={notesId} contrast={7}>{notes}</Text.span></>;
  return <Card flat className="studio-script-block" noBodyPadding>
    <div className="studio-script-block__label">
      <Text.caption contrast={5}>{String(index + 1).padStart(2, '0')}</Text.caption><Text.label contrast={7}>{kind}</Text.label>
      {!hasNotes && !readOnly && <Button className="studio-script-block__add-notes" variant="text" color="overlay" size="micro" icon={<IconVideoPlus />}
        tooltip={t('addNotes')} aria-label={`${t('addNotes')} · ${kind}`} onClick={() => setEditing(true)} />}
    </div>
    <Input multiline rows={1} fitContent readOnly={readOnly} value={block.spoken_text} onInput={text => s.editScript(block.id, 'spoken_text', text)} aria-label={`${t('spoken')} · ${kind}`} maxLength={12000} />
    {hasNotes && (readOnly
      ? <div className="studio-script-block__notes is-static">{note}</div>
      : editing
        ? <div className="studio-script-block__notes is-editing">
          <IconVideo className="studio-script-block__notes-icon" aria-hidden="true" />
          <Input multiline rows={1} fitContent autoFocus value={notes} onFocus={caretToEnd} onInput={text => s.editScript(block.id, 'recording_notes', text)}
            onBlur={() => setEditing(false)} onKeyDown={event => { if (event.key === 'Escape') event.currentTarget.blur(); }}
            aria-label={`${t('notes')} · ${kind}`} maxLength={5000} />
        </div>
        : <button type="button" className="studio-script-block__notes" aria-label={`${t('editNotes')} · ${kind}`} aria-describedby={notesId} onClick={() => setEditing(true)}>{note}</button>)}
  </Card>;
}

// Every `studio` field a block reads: an edit renders the block it is in, not the others.
export default memoStudio(ScriptBlock, ['t', 'editScript']);
